import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Linkedin, Facebook, Instagram, Send, CheckCircle } from 'lucide-react';
import { companyInfo, colors, servicesList } from '../data';

const Contact = () => {
  const [status, setStatus] = useState(null); // 'loading', 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="py-20">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h1 className={`text-4xl md:text-6xl font-serif font-bold ${colors.contrast} mb-8`}>
              Lad os tage en <span className="italic font-normal">dialog</span>
            </h1>
            <p className="text-lg text-stone-600 mb-12 leading-relaxed">
              Uanset om du har brug for ledelsesrådgivning, sparring eller en workshop til dit team, er jeg klar til at tage en uforpligtende samtale om jeres behov.
            </p>

            <div className="space-y-8 mb-12">
              <div className="flex items-start space-x-6">
                <div className={`w-12 h-12 ${colors.accent} rounded-full flex items-center justify-center flex-shrink-0 text-stone-700`}>
                  <Phone size={20} />
                </div>
                <div>
                  <p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Ring til mig</p>
                  <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className={`text-xl font-serif font-bold ${colors.contrast} hover:${colors.gold} transition-colors`}>
                    {companyInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className={`w-12 h-12 ${colors.accent} rounded-full flex items-center justify-center flex-shrink-0 text-stone-700`}>
                  <Mail size={20} />
                </div>
                <div>
                  <p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-1">Send en mail</p>
                  <a href={`mailto:${companyInfo.email}`} className={`text-xl font-serif font-bold ${colors.contrast} hover:${colors.gold} transition-colors`}>
                    {companyInfo.email}
                  </a>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs text-stone-400 uppercase tracking-widest font-bold mb-6">Følg med her</p>
              <div className="flex space-x-4">
                {[
                  { icon: Linkedin, href: companyInfo.socials.linkedin },
                  { icon: Facebook, href: companyInfo.socials.facebook },
                  { icon: Instagram, href: companyInfo.socials.instagram }
                ].map((social, i) => (
                  <a 
                    key={i}
                    href={social.href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center text-stone-400 hover:${colors.gold} hover:border-current transition-all`}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
            </div>
            
            <div className="mt-12 pt-12 border-t border-stone-100">
              <p className="text-sm text-stone-500">
                Meyer Management ApS<br />
                CVR: {companyInfo.cvr}
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl shadow-stone-100 border border-stone-100"
          >
            {status === 'success' ? (
              <div className="text-center py-12">
                <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
                <h2 className={`text-2xl font-serif font-bold ${colors.contrast} mb-4`}>Tak for din besked!</h2>
                <p className="text-stone-600 mb-8">Jeg vender tilbage til dig så hurtigt som muligt.</p>
                <button 
                  onClick={() => setStatus(null)}
                  className={`px-8 py-3 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50 transition-all`}
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot */}
                <div className="sr-only absolute opacity-0 -z-10">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-stone-700 mb-2">Navn</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      required 
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-stone-100 focus:border-stone-400 outline-none transition-all"
                      placeholder="Dit fulde navn"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-stone-700 mb-2">E-mail</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-stone-100 focus:border-stone-400 outline-none transition-all"
                      placeholder="din@mail.dk"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-stone-700 mb-2">Telefonnummer</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    required 
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-stone-100 focus:border-stone-400 outline-none transition-all"
                    placeholder="Dit telefonnummer"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-bold text-stone-700 mb-2">Emne</label>
                  <select 
                    id="subject" 
                    name="subject" 
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-stone-100 focus:border-stone-400 outline-none transition-all bg-white"
                  >
                    {servicesList.map((service) => (
                      <option key={service.name}>{service.name}</option>
                    ))}
                    <option>Andet</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-stone-700 mb-2">Besked</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="5" 
                    required 
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-stone-100 focus:border-stone-400 outline-none transition-all resize-none"
                    placeholder="Hvordan kan jeg hjælpe dig?"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={status === 'loading'}
                  className={`w-full py-4 rounded-full ${colors.contrastBg} text-white font-bold flex items-center justify-center space-x-2 hover:opacity-90 transition-all disabled:opacity-50`}
                >
                  {status === 'loading' ? (
                    <span>Sender...</span>
                  ) : (
                    <>
                      <span>Send besked</span>
                      <Send size={18} />
                    </>
                  )}
                </button>
                
                {status === 'error' && (
                  <p className="text-red-500 text-sm text-center">Der opstod en fejl. Prøv venligst igen eller ring direkte.</p>
                )}
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
